import { NextSeo } from 'next-seo';
import SEO from 'next-seo.config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useRef } from 'react';
import StickyBox from 'react-sticky-box';

import Image from 'src/components/image';
import Layout from 'src/components/layouts';
import client from 'src/utils/client';
// import EmptyArticle from 'src/components/empty';
import parse from 'html-react-parser';
import _ from 'lodash';
import PageError from 'pages/404';
import CardH from 'src/components/cards/card-h';
import WidgetLottoCheck from 'src/components/widgets/widget-lotto-check';
import WidgetLottoResult from 'src/components/widgets/widget-lotto-result';
import { APP_IMG, APP_URL, SHOW_COMMEMORATE } from 'src/constants';
import { IMAGE_NAME, WEB_NAME, WEB_URL } from 'src/constants/layout';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { DATALAYER_TYPE } from 'src/utils/datalayer/send-datalayer';
import { useDesktop } from 'src/utils/device';
import { escapeChar } from 'src/utils/helper';
import { GET } from 'src/utils/services';
import useUser from 'src/utils/useUser';
import WidgetHeading from 'src/2.0/widgets/widget-heading';

const PageLotteryDetail = ({ article, lastNews, meta, dataAdsHeader }) => {
  const _data = useUser();
  const sessionSevId = _data?.user ? decryptionClient(_data?.user) : null;
  // console.log('PageLotteryDetail');
  if (!article) {
    return <PageError sessionSevId={sessionSevId} />;
  }
  const router = useRouter();
  const footerRef = useRef(false);

  const _article = {
    ...article,
    title: meta?.title,
    description: meta?.description,
    keyword: meta?.keyword
  };
  const articleTitle = escapeChar(meta?.title);
  const articleDescription = escapeChar(meta?.description);
  const shareLink = router.asPath;
  const baseUrlCategory = `${APP_URL}${shareLink}`;

  return (
    <>
      <NextSeo
        {...SEO}
        title={_article?.title}
        description={_article?.description}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
        openGraph={{
          ...SEO.openGraph,
          title: _article?.title,
          description: _article?.description,
          url: baseUrlCategory,
          images: [
            {
              url: `${APP_IMG}${_article?.image}`,
              width: '700',
              height: '395',
              alt: _article?.title
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article?.keyword
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={`${APP_URL}${shareLink}`} />
        <meta property='twitter:title' content={_article?.title} />
        <meta property='twitter:description' content={_article?.description} />
        <meta property='twitter:image' content={`${APP_IMG}${article.image}`} />
        {/* ----------------------------- ScriptBrowser ------------------------------------ */}
        {!useDesktop() && !_.isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_mobile}`) : ''}
        {useDesktop() && !_.isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_desktop}`) : ''}
        {/* ----------------------------- ADS : PPN ------------------------------- */}
        <script async type='text/javascript' src='https://securepubads.g.doubleclick.net/tag/js/gpt.js' />
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: 'var googletag = googletag || {}; window.googletag.cmd = googletag.cmd || [];'
          }}
        />

        {/* --------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                __th_page="lottery_detail";
              `
          }}
        />

        {/* ---------------------------- SPIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "https://schema.org",
                  "@type": "WebSite",
                  "name": "${WEB_NAME}",
                  "url": "${WEB_URL.WEB}",
                  "description": "${articleDescription}",
                  "sameAs": [
                    "${WEB_URL.FACEBOOK}",
                    "${WEB_URL.YOUTUBE}",
                    "${WEB_URL.TWITTER}",
                    "${WEB_URL.LINE}"
                  ],
                  "potentialAction": {
                    "@type": "SearchAction",
                    "target": "${WEB_URL.WEB}search?q={search_term_string}",
                    "query-input": "required name=search_term_string"
                  }
                }
              `
          }}
        />

        {/* ---------------------------- SPIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "http://schema.org",
                  "@type": "WebPage",
                  "name": "${articleTitle}",
                  "description": "${articleDescription}",
                  "publisher": {
                    "@type": "ProfilePage",
                    "name": "${WEB_NAME}"
                  }
                }
              `
          }}
        />

        {/* ------------------------ SPIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "http://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {
                      "@type": "ListItem",
                      "position": 1,
                      "name": "หน้าแรก",
                      "item": "${APP_URL}"
                    },
                    {
                      "@type": "ListItem",
                      "position": 2,
                      "name": "lottery",
                      "item": "${APP_URL}/lottery"
                    },
                    {
                      "@type": "ListItem",
                      "position": 3,
                      "name": "${articleTitle}",
                      "item": "${APP_URL}${shareLink}"
                    }
                  ]
                }
              `
          }}
        />

        {/* -------------------------- SPIPPET : NewsArticle ------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "http://schema.org",
                  "@type": "NewsArticle",
                  "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": "${APP_URL}/lottery/${article.dateDefault}"
                  },
                  "headline": "${articleTitle}",
                  "alternativeHeadline": "${articleTitle}",
                  "description": "${articleDescription}",
                  "image": "${APP_IMG}${article.image}",
                  "author": {
                    "@type": "Thing",
                    "name": "${WEB_NAME}",
                    "url": "${APP_URL}"
                  },
                  "publisher": {
                    "@type": "Organization",
                    "name": "${WEB_NAME}",
                    "logo": {
                      "@type": "ImageObject",
                      "url": "${APP_IMG}/static/${IMAGE_NAME}"
                    }
                  }
                }
              `
          }}
        />
        {/* <img
              width='99999'
              height='99999'
              alt='background-default'
              title='background-default'
              className='background-default'
              style={{
                pointerEvents: 'none',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '99vw',
                height: '99vh',
                maxWidth: '99vw',
                maxHeight: '99vh'
              }}
              src='data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIHdpZHRoPSI5OTk5OXB4IiBoZWlnaHQ9Ijk5OTk5cHgiIHZpZXdCb3g9IjAgMCA5OTk5OSA5OTk5OSIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIj48ZyBzdHJva2U9Im5vbmUiIGZpbGw9Im5vbmUiIGZpbGwtb3BhY2l0eT0iMCI+PHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9Ijk5OTk5IiBoZWlnaHQ9Ijk5OTk5Ij48L3JlY3Q+IDwvZz4gPC9zdmc+'
            ></img> */}
      </Head>
      <Layout sessionSevId={sessionSevId}>
        <div id='section-1' className='detail-wrapper' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
          <div className='container'>
            <ul className='breadcrumb'>
              <li>
                <a href='/'>หน้าแรก</a>
              </li>
              <li>{article?.title}</li>
              {/* <li>{article.title}</li> */}
            </ul>
            <div className='row'>
              <div className='col-lg-8 col-md-12'>
                <div className='main-content'>
                  <h1 className='content-title'>{article?.title}</h1>
                </div>
                <div className='d-none d-md-block'>
                  <WidgetLottoResult />
                </div>
                <div className='img-fluid'>
                  <Image image={article.image} title={article.title} ignoreLazy={false} type='FEATURE_IMAGE' />
                </div>
                {/* ---------------------- Dark Box ---------------------- */}
                <div className='lottery-dark-box'>
                  <div className='row'>
                    <div className='col-12 dark-box-heading text-center'>
                      <p>ผลรางวัลสลากกินแบ่งรัฐบาล</p>
                      <span>{article.title}</span>
                    </div>
                  </div>

                  <div className='row mt-3'>
                    <div className='col-12 col-md-6 lottery-box-item'>
                      <div className='row justify-content-start mx-auto'>
                        <div className='col-auto lottery-box-item-title'>
                          <h2>รางวัลที่ 1</h2>
                          <h3>รางวัลละ 6,000,000 บาท</h3>
                        </div>
                        <div className='col-auto lottery-box-item-reward'>
                          <h4>
                            <strong>{article.firstAward}</strong>
                          </h4>
                        </div>
                      </div>
                    </div>
                    <div className='col-12 col-md-6 lottery-box-item'>
                      <div className='row justify-content-start mx-auto'>
                        <div className='col-auto lottery-box-item-title'>
                          <h2>เลขท้าย 2 ตัว</h2>
                          <h3>รางวัลละ 2,000 บาท</h3>
                        </div>
                        <div className='col-auto lottery-box-item-reward'>
                          <h4>
                            <strong>{article.lastTwoNumber}</strong>
                          </h4>
                        </div>
                      </div>
                    </div>
                    <div className='col-12 col-md-6 lottery-box-item'>
                      <div className='row justify-content-start mx-auto'>
                        <div className='col-auto lottery-box-item-title'>
                          <h2>เลขหน้า 3 ตัว</h2>
                          <h3>รางวัลละ 4,000 บาท</h3>
                        </div>
                        <div className='col-auto lottery-box-item-reward'>
                          <h4>
                            {article.frontThreeNumber.map((item, i) => (
                              <span key={i}>{item}</span>
                            ))}
                          </h4>
                        </div>
                      </div>
                    </div>
                    <div className='col-12 col-md-6 lottery-box-item'>
                      <div className='row justify-content-start mx-auto'>
                        <div className='col-auto lottery-box-item-title'>
                          <h2>เลขท้าย 3 ตัว</h2>
                          <h3>รางวัลละ 4,000 บาท</h3>
                        </div>
                        <div className='col-auto lottery-box-item-reward'>
                          <h4>
                            {article.behindThreeNumber.map((item, i) => (
                              <span key={i}>{item}</span>
                            ))}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='d-block d-md-none my-4'>
                  <WidgetLottoResult />
                  <WidgetLottoCheck
                    //! DATALAYER
                    eventDataLayer={{
                      type: DATALAYER_TYPE.TRACK_POSITION,
                      router: router?.pathname,
                      section: 'ตรวจผลสลากกินแบ่งรัฐบาล',
                      position: 'ใต้:header'
                    }}
                  />
                </div>

                {/* ---------------------- Light Box ---------------------- */}
                <div className='col-12 lottery-light-box'>
                  <div className='row'>
                    <div className='col-12 lottery-box-item-title'>
                      <h5>รางวัลที่ 2</h5>
                      <h6>รางวัลละ 200,000 บาท</h6>
                    </div>
                    <div className='col-12 lottery-box-item-reward'>
                      <div className='row justify-content-center'>
                        {article.secondAward.map((item, index) => (
                          <div key={index} className='col-4 col-md-3 col-lg-2'>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='col-12 lottery-light-box'>
                  <div className='row'>
                    <div className='col-12 lottery-box-item-title'>
                      <h5>รางวัลที่ 3</h5>
                      <h6>รางวัลละ 80,000 บาท</h6>
                    </div>
                    <div className='col-12 lottery-box-item-reward'>
                      <div className='row'>
                        {article.thirdAward.map((item, index) => (
                          <div key={index} className='col-4 col-md-3 col-lg-2'>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='col-12 lottery-light-box'>
                  <div className='row'>
                    <div className='col-12 lottery-box-item-title'>
                      <h5>รางวัลที่ 4</h5>
                      <h6>รางวัลละ 40,000 บาท</h6>
                    </div>
                    <div className='col-12 lottery-box-item-reward'>
                      <div className='row'>
                        {article.fourAward.map((item, index) => (
                          <div key={index} className='col-4 col-md-3 col-lg-2'>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='col-12 lottery-light-box'>
                  <div className='row'>
                    <div className='col-12 lottery-box-item-title'>
                      <h5>รางวัลที่ 5</h5>
                      <h6>รางวัลละ 20,000 บาท</h6>
                    </div>
                    <div className='col-12 lottery-box-item-reward'>
                      <div className='row'>
                        {article.fifAward.map((item, index) => (
                          <div key={index} className='col-4 col-md-3 col-lg-2'>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {/* ------------------------ End Light Box ----------------------- */}
              </div>

              {/* ------------------------ Col Right ----------------------- */}
              <div className='col-lg-4 d-none d-md-block'>
                <StickyBox offsetTop={70} offsetBottom={70}>
                  <WidgetLottoCheck
                    //! DATALAYER
                    eventDataLayer={{
                      type: DATALAYER_TYPE.TRACK_POSITION,
                      router: router?.pathname,
                      section: 'ตรวจผลสลากกินแบ่งรัฐบาล',
                      position: 'ใต้:header'
                    }}
                  />
                  <div className='widget-content'>
                    <WidgetHeading heading='Latest News' />
                    <div className='block-content'>
                      {Array.isArray(lastNews) && lastNews.length < 1 ? (
                        <div className='no-article'>
                          <img className='--side' src={`${APP_IMG}/no-article.png`} alt='no-article' width='100%' />
                        </div>
                      ) : (
                        <ul className='block-list'>
                          {lastNews.slice(0, 4).map((item, index) => (
                            <li key={index}>
                              <CardH
                                data={item}
                                large={false}
                                eventDataLayer={{
                                  type: DATALAYER_TYPE.TRACK_POSITION,
                                  router: router?.pathname,
                                  section: 'ข่าLatest Newsวล่าสุด',
                                  position: 'ใต้:ตรวจผลสลากกินแบ่งรัฐบาล',
                                  data: {
                                    block: '2',
                                    title: item.title,
                                    heading: 'Latest News',
                                    index: index + 1
                                  }
                                }}
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </StickyBox>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export async function getServerSideProps({ req, res, params }) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    article: null,
    lastNews: null,
    meta: null,
    dataAdsHeader: null
  };
  const scriptBrowser = await GET('/api/headerAds');
  if (!_.isEmpty(scriptBrowser)) {
    initProps.dataAdsHeader = scriptBrowser || '';
  }
  const regex = /([1-9]([0-9]{3})-([1][0-2]|[0][0-9])-([0-2][0-9]|[3][0-1]))/;
  const { id } = params;

  const checkId = regex.test(id);

  if (checkId) {
    try {
      const { data } = await client.fetch.get(`/api/v1.0/lottory/${params.id}`);
      initProps.article = data.data;
      initProps.lastNews = data.lastNews;
      initProps.meta = data.meta;
    } catch (err) {
      console.log('==========> CATEGORY ERROR', err);
      initProps.errMessage = err.message;
      initProps.statusCode = err?.response?.status || 500;

      res.statusCode = initProps.statusCode;
      res.end();
    }
  } else {
    res.setHeader('location', '/lottery');
    res.statusCode = 302;
    res.end();
  }

  return {
    props: initProps
  };
}

export default PageLotteryDetail;
